import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { format, startOfMonth, endOfMonth, subDays, startOfYear } from "date-fns";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { zar } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";

const EMPTY_ENTRY = { food_sales: 0, beverage_sales: 0, revenue_additional: 0, tips_pos_cash: 0, tips_pos_card: 0, tips_add_cash: 0, tips_add_card: 0, covers_lunch: "", covers_dinner: "" };
const FIELDS = [
  ["food_sales", "Food sales (POS)"],
  ["beverage_sales", "Beverage sales (POS)"],
  ["revenue_additional", "Revenue — additional"],
  ["tips_pos_cash", "Tips POS — cash"],
  ["tips_pos_card", "Tips POS — card"],
  ["tips_add_cash", "Additional tips — cash"],
  ["tips_add_card", "Additional tips — card"],
];
const COVER_FIELDS = [
  ["covers_lunch", "Lunch covers"],
  ["covers_dinner", "Dinner covers"],
];

function defaultRange(group) {
  const t = new Date();
  const iso = (d) => format(d, "yyyy-MM-dd");
  if (group === "weekly") return [iso(subDays(t, 83)), iso(t)];
  if (group === "monthly") return [iso(startOfYear(t)), iso(t)];
  if (group === "annual") return ["2025-01-01", iso(t)];
  return [iso(startOfMonth(t)), iso(endOfMonth(t))];
}

function Reports() {
  const [groupBy, setGroupBy] = useState("daily");
  const [range, setRange] = useState(defaultRange("daily"));
  const [report, setReport] = useState(null);

  const load = () =>
    api.get(`/revenue/report?group_by=${groupBy}&start=${range[0]}&end=${range[1]}`)
      .then((r) => setReport(r.data))
      .catch(() => toast.error("Failed to load report"));

  useEffect(() => { load(); }, [groupBy, range]);

  const switchGroup = (g) => {
    setGroupBy(g);
    setRange(defaultRange(g));
  };

  const totals = (report?.rows || []).reduce(
    (a, r) => ({
      days: a.days + r.days, revenue: a.revenue + r.revenue,
      tips_total: a.tips_total + r.tips_total,
      food_sales: a.food_sales + r.food_sales, beverage_sales: a.beverage_sales + r.beverage_sales,
      food_spend: a.food_spend + r.food_spend, bev_spend: a.bev_spend + r.bev_spend,
      other_spend: a.other_spend + r.other_spend, total_spend: a.total_spend + r.total_spend,
      theoretical_cost: a.theoretical_cost + (r.theoretical_cost || 0),
    }),
    { days: 0, revenue: 0, tips_total: 0, food_sales: 0, beverage_sales: 0, food_spend: 0, bev_spend: 0, other_spend: 0, total_spend: 0, theoretical_cost: 0 }
  );

  const pct = (num, den) => (den ? `${((num / den) * 100).toFixed(1)}%` : "—");

  return (
    <div className="space-y-4" data-testid="reports-view">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <Tabs value={groupBy} onValueChange={switchGroup}>
          <TabsList data-testid="report-group-tabs">
            <TabsTrigger value="daily" data-testid="group-daily">Daily</TabsTrigger>
            <TabsTrigger value="weekly" data-testid="group-weekly">Weekly</TabsTrigger>
            <TabsTrigger value="monthly" data-testid="group-monthly">Monthly</TabsTrigger>
            <TabsTrigger value="annual" data-testid="group-annual">Annual</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="flex items-end gap-3">
          <div className="space-y-1"><Label className="text-xs">From</Label><Input type="date" data-testid="report-start" value={range[0]} onChange={(e) => setRange([e.target.value, range[1]])} /></div>
          <div className="space-y-1"><Label className="text-xs">To</Label><Input type="date" data-testid="report-end" value={range[1]} onChange={(e) => setRange([range[0], e.target.value])} /></div>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        {[
          ["Revenue", zar(totals.revenue), "report-total-revenue"],
          ["Tips total", zar(totals.tips_total), "report-total-tips"],
          ["Food cost %", pct(totals.food_spend, totals.food_sales), "report-food-cost"],
          ["Beverage cost %", pct(totals.bev_spend, totals.beverage_sales), "report-bev-cost"],
          ["Other spend %", pct(totals.other_spend, totals.revenue), "report-other-pct"],
          ["Overall spend %", pct(totals.total_spend, totals.revenue), "report-spend-pct"],
          ["Theo. food cost %", pct(totals.theoretical_cost, totals.revenue), "report-theo-cost"],
        ].map(([label, val, tid]) => (
          <Card key={tid} className="border-border shadow-none" data-testid={tid}>
            <CardContent className="pt-5">
              <p className="text-xs text-muted-foreground">{label}</p>
              <p className="font-heading text-2xl font-bold mt-1">{val}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="glass border border-border rounded-lg overflow-x-auto">
        <table className="w-full text-sm min-w-[1100px]" data-testid="report-table">
          <thead>
            <tr className="border-b text-left text-muted-foreground">
              <th className="px-4 py-3 font-medium">Period</th>
              <th className="px-4 py-3 font-medium text-right">Days</th>
              <th className="px-4 py-3 font-medium text-right">Food sales</th>
              <th className="px-4 py-3 font-medium text-right">Bev sales</th>
              <th className="px-4 py-3 font-medium text-right">Revenue</th>
              <th className="px-4 py-3 font-medium text-right">Tips</th>
              <th className="px-4 py-3 font-medium text-right">Food cost %</th>
              <th className="px-4 py-3 font-medium text-right">Bev cost %</th>
              <th className="px-4 py-3 font-medium text-right">Other %</th>
              <th className="px-4 py-3 font-medium text-right">Spend %</th>
              <th className="px-4 py-3 font-medium text-right">Theo %</th>
            </tr>
          </thead>
          <tbody>
            {(report?.rows || []).map((r) => (
              <tr key={r.period} className="border-b last:border-0" data-testid={`report-row-${r.period}`}>
                <td className="px-4 py-2.5 font-medium">{r.period}</td>
                <td className="px-4 py-2.5 num">{r.days}</td>
                <td className="px-4 py-2.5 num">{zar(r.food_sales)}</td>
                <td className="px-4 py-2.5 num">{zar(r.beverage_sales)}</td>
                <td className="px-4 py-2.5 num font-medium">{zar(r.revenue)}</td>
                <td className="px-4 py-2.5 num">{zar(r.tips_total)}</td>
                <td className="px-4 py-2.5 num">{r.food_cost_pct != null ? `${r.food_cost_pct}%` : "—"}</td>
                <td className="px-4 py-2.5 num">{r.bev_cost_pct != null ? `${r.bev_cost_pct}%` : "—"}</td>
                <td className="px-4 py-2.5 num">{r.other_spend_pct != null ? `${r.other_spend_pct}%` : "—"}</td>
                <td className="px-4 py-2.5 num font-medium">{r.spend_pct != null ? `${r.spend_pct}%` : "—"}</td>
                <td className="px-4 py-2.5 num" data-testid={`report-theo-${r.period}`}>{r.theoretical_cost_pct != null ? `${r.theoretical_cost_pct}%` : "—"}</td>
              </tr>
            ))}
            {report && report.rows.length === 0 && (
              <tr><td colSpan={11} className="px-4 py-8 text-center text-muted-foreground">No entries in this range.</td></tr>
            )}
          </tbody>
          {report && report.rows.length > 0 && (
            <tfoot>
              <tr className="border-t bg-accent/50 font-semibold" data-testid="report-totals-row">
                <td className="px-4 py-2.5">Total</td>
                <td className="px-4 py-2.5 num">{totals.days}</td>
                <td className="px-4 py-2.5 num">{zar(totals.food_sales)}</td>
                <td className="px-4 py-2.5 num">{zar(totals.beverage_sales)}</td>
                <td className="px-4 py-2.5 num">{zar(totals.revenue)}</td>
                <td className="px-4 py-2.5 num">{zar(totals.tips_total)}</td>
                <td className="px-4 py-2.5 num">{pct(totals.food_spend, totals.food_sales)}</td>
                <td className="px-4 py-2.5 num">{pct(totals.bev_spend, totals.beverage_sales)}</td>
                <td className="px-4 py-2.5 num">{pct(totals.other_spend, totals.revenue)}</td>
                <td className="px-4 py-2.5 num">{pct(totals.total_spend, totals.revenue)}</td>
                <td className="px-4 py-2.5 num" data-testid="report-theo-total">{pct(totals.theoretical_cost, totals.revenue)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}

function Capture({ canViewDist }) {
  const [date, setDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [form, setForm] = useState(EMPTY_ENTRY);
  const [entries, setEntries] = useState([]);
  const [functionsByDay, setFunctionsByDay] = useState({});
  const [dist, setDist] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadMonth = () => {
    const s = format(startOfMonth(new Date(date + "T00:00:00")), "yyyy-MM-dd");
    const e = format(endOfMonth(new Date(date + "T00:00:00")), "yyyy-MM-dd");
    api.get(`/revenue?start=${s}&end=${e}`).then((r) => {
      setEntries(r.data.entries);
      const fbd = r.data.functions_by_day;
      // Backend returns a list [{date, functions_revenue}]; normalize to a map {date: amount}
      setFunctionsByDay(Array.isArray(fbd)
        ? Object.fromEntries(fbd.map((x) => [x.date, x.functions_revenue]))
        : (fbd || {}));
    }).catch(() => {});
  };

  const loadDay = () => {
    api.get(`/revenue?start=${date}&end=${date}`).then((r) => {
      const entry = r.data.entries[0];
      setForm(entry ? { ...EMPTY_ENTRY, ...Object.fromEntries(FIELDS.map(([k]) => [k, entry[k] || 0])),
                        covers_lunch: entry.covers_lunch ?? "", covers_dinner: entry.covers_dinner ?? "" } : EMPTY_ENTRY);
    }).catch(() => {});
    if (canViewDist) {
      api.get(`/tips/distribution?date=${date}`).then((r) => setDist(r.data)).catch(() => setDist(null));
    } else {
      setDist(null);
    }
  };

  useEffect(() => { loadMonth(); loadDay(); }, [date]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = Object.fromEntries(Object.entries(form).map(([k, v]) =>
        [k, k.startsWith("covers") ? (v === "" || v == null ? null : Number(v)) : Number(v) || 0]));
      const { data } = await api.put(`/revenue/${date}`, payload);
      if (data.distribution) setDist(data.distribution);
      toast.success(canViewDist ? "Saved — tip distribution recalculated" : "Saved — tip distribution recalculated (visible to owner)");
      loadMonth();
    } catch (e) {
      toast.error(apiError(e, "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  const totalTips = (Number(form.tips_pos_cash) || 0) + (Number(form.tips_pos_card) || 0) + (Number(form.tips_add_cash) || 0) + (Number(form.tips_add_card) || 0);
  const totalRev = (Number(form.food_sales) || 0) + (Number(form.beverage_sales) || 0) + (Number(form.revenue_additional) || 0);
  const totalCovers = (Number(form.covers_lunch) || 0) + (Number(form.covers_dinner) || 0);
  const functionsRev = Number(functionsByDay[date] || entries.find((e) => e.date === date)?.functions_revenue || 0);

  const tableRows = [...entries];
  Object.entries(functionsByDay).forEach(([d, amt]) => {
    if (Number(amt) > 0 && !tableRows.some((e) => e.date === d)) tableRows.push({ date: d, functions_revenue: amt });
  });
  tableRows.sort((a, b) => (a.date < b.date ? -1 : 1));

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <Card className={`${canViewDist ? "lg:col-span-5" : "lg:col-span-12"} border-border shadow-none`} data-testid="capture-card">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Daily entry</CardTitle>
            <Input type="date" data-testid="capture-date" value={date} onChange={(e) => setDate(e.target.value)} className="w-48" />
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              {FIELDS.map(([k, label]) => (
                <div key={k} className="space-y-1.5">
                  <Label className="text-xs">{label}</Label>
                  <Input type="number" step="any" min="0" data-testid={`capture-${k}`} value={form[k]}
                    onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
                </div>
              ))}
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">Covers (optional)</p>
              <div className="grid grid-cols-2 gap-4">
                {COVER_FIELDS.map(([k, label]) => (
                  <div key={k} className="space-y-1.5">
                    <Label className="text-xs">{label}</Label>
                    <Input type="number" step="1" min="0" inputMode="numeric" placeholder="—" data-testid={`capture-${k}`} value={form[k]}
                      onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
                  </div>
                ))}
              </div>
            </div>
            <div className="flex justify-between text-sm border-t pt-3">
              <span className="text-muted-foreground">Total revenue</span>
              <span className="font-semibold num" data-testid="capture-total-revenue">{zar(totalRev)}</span>
            </div>
            {functionsRev > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Functions (recognized this day)</span>
                <span className="num font-medium text-[#C9A8FF]" data-testid="capture-functions-revenue">{zar(functionsRev)}</span>
              </div>
            )}
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Total tips (cash {zar((Number(form.tips_pos_cash)||0)+(Number(form.tips_add_cash)||0))} / card {zar((Number(form.tips_pos_card)||0)+(Number(form.tips_add_card)||0))})</span>
              <span className="font-semibold num" data-testid="capture-total-tips">{zar(totalTips)}</span>
            </div>
            {totalCovers > 0 && (
              <div className="border-t pt-3 space-y-1.5" data-testid="capture-spend-per-head">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Total covers</span>
                  <span className="font-semibold num" data-testid="capture-total-covers">{totalCovers}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Avg food spend / head</span>
                  <span className="num" data-testid="capture-spend-food">{zar((Number(form.food_sales) || 0) / totalCovers)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Avg beverage spend / head</span>
                  <span className="num" data-testid="capture-spend-bev">{zar((Number(form.beverage_sales) || 0) / totalCovers)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Avg total spend / head</span>
                  <span className="font-semibold num text-[#C9A8FF]" data-testid="capture-spend-total">{zar(totalRev / totalCovers)}</span>
                </div>
                <p className="text-[11px] text-muted-foreground">Per-head figures exclude tips; total includes additional revenue.</p>
              </div>
            )}
            <Button onClick={save} disabled={saving} data-testid="capture-save-btn" className="w-full bg-primary hover:bg-[#8B5CF6] text-white">
              {saving ? "Saving…" : "Save & distribute tips"}
            </Button>
          </CardContent>
        </Card>

        {canViewDist && (
        <Card className="lg:col-span-7 border-border shadow-none" data-testid="distribution-card">
          <CardHeader><CardTitle className="font-heading text-lg">Tip distribution — {date}</CardTitle></CardHeader>
          <CardContent>
            {!dist || dist.empty ? (
              <p className="text-sm text-muted-foreground" data-testid="distribution-empty">No entry for this date yet. Save figures to run the distribution.</p>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div><p className="text-muted-foreground text-xs">Total tips</p><p className="font-semibold num text-left" data-testid="dist-total-tips">{zar(dist.total_tips)}</p></div>
                  <div><p className="text-muted-foreground text-xs">Deductions ({dist.deduction_pct_total}%)</p><p className="font-semibold num text-left text-[#F87171]" data-testid="dist-deductions">−{zar(dist.deductions_total)}</p></div>
                  <div><p className="text-muted-foreground text-xs">Balance to distribute</p><p className="font-semibold num text-left text-[#A9C97E]" data-testid="dist-balance">{zar(dist.balance)}</p></div>
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-muted-foreground">
                      <th className="py-2 font-medium">Category</th>
                      <th className="py-2 font-medium text-right">%</th>
                      <th className="py-2 font-medium text-right">Pool</th>
                      <th className="py-2 font-medium text-right">Staff</th>
                      <th className="py-2 font-medium text-right">Per person</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dist.categories.map((c) => (
                      <tr key={c.category} className="border-b last:border-0" data-testid={`dist-cat-${c.category}`}>
                        <td className="py-2">{c.label}</td>
                        <td className="py-2 num">{c.pct}%</td>
                        <td className="py-2 num">{zar(c.pool)}</td>
                        <td className="py-2 num">{c.headcount}</td>
                        <td className="py-2 num font-semibold">{c.headcount ? zar(c.per_employee) : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {dist.payouts?.length > 0 && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-2">Per-employee payouts</p>
                    <div className="flex flex-wrap gap-2">
                      {dist.payouts.map((p) => (
                        <span key={p.id} className="text-xs bg-accent rounded-full px-3 py-1" data-testid={`payout-${p.employee_id}`}>
                          {p.employee_name} · <strong>{zar(p.amount)}</strong>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
        )}
      </div>

      <Card className="border-border shadow-none" data-testid="entries-table">
        <CardHeader><CardTitle className="font-heading text-lg">This month</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead>
              <tr className="border-b text-left text-muted-foreground">
                <th className="py-2 pr-4 font-medium">Date</th>
                <th className="py-2 pr-4 font-medium text-right">Revenue</th>
                <th className="py-2 pr-4 font-medium text-right">Covers</th>
                <th className="py-2 pr-4 font-medium text-right">Avg spend / head</th>
                <th className="py-2 pr-4 font-medium text-right">Functions</th>
                <th className="py-2 pr-4 font-medium text-right">Tips (cash)</th>
                <th className="py-2 pr-4 font-medium text-right">Tips (card)</th>
                <th className="py-2 font-medium text-right">Total tips</th>
              </tr>
            </thead>
            <tbody>
              {tableRows.map((e) => {
                const cash = (e.tips_pos_cash || 0) + (e.tips_add_cash || 0);
                const card = (e.tips_pos_card || 0) + (e.tips_add_card || 0);
                const rev = (e.food_sales ?? e.revenue_pos ?? 0) + (e.beverage_sales || 0) + (e.revenue_additional || 0);
                return (
                  <tr key={e.date} className="border-b last:border-0 cursor-pointer hover:bg-accent/60" data-testid={`entry-${e.date}`} onClick={() => setDate(e.date)}>
                    <td className="py-2 pr-4 font-medium">{e.date}</td>
                    <td className="py-2 pr-4 num">{zar(rev)}</td>
                    <td className="py-2 pr-4 num" data-testid={`entry-covers-${e.date}`}>{e.spend ? e.spend.covers : "—"}</td>
                    <td className="py-2 pr-4 num" data-testid={`entry-spend-${e.date}`}>{e.spend ? zar(e.spend.total) : "—"}</td>
                    <td className="py-2 pr-4 num" data-testid={`entry-functions-${e.date}`}>{(e.functions_revenue || 0) > 0 ? zar(e.functions_revenue) : "—"}</td>
                    <td className="py-2 pr-4 num">{zar(cash)}</td>
                    <td className="py-2 pr-4 num">{zar(card)}</td>
                    <td className="py-2 num">{zar(cash + card)}</td>
                  </tr>
                );
              })}
              {tableRows.length === 0 && <tr><td colSpan={8} className="py-8 text-center text-muted-foreground">No entries this month.</td></tr>}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

export default function Revenue({ embedded = false }) {
  const { can, user } = useAuth();
  const canViewDist = can("view_tip_distribution");
  const isOwner = user.role === "owner";
  const [params, setParams] = useSearchParams();
  const sub = params.get("rsub") || (isOwner ? "overview" : "input");

  return (
    <div className="space-y-6" data-testid="revenue-page">
      {!embedded && (
        <div>
          <h1 className="font-heading text-3xl font-bold">Revenue &amp; Tips</h1>
          <p className="text-muted-foreground mt-1">Sales and tips, with manual input and POS-fed entries</p>
        </div>
      )}
      <Tabs value={sub} onValueChange={(v) => setParams(v === "overview" ? { tab: "revenue" } : { tab: "revenue", rsub: v })}>
        <TabsList data-testid="revenue-tabs">
          {isOwner && <TabsTrigger value="overview" data-testid="tab-reports">Overview</TabsTrigger>}
          <TabsTrigger value="input" data-testid="tab-capture">Input</TabsTrigger>
        </TabsList>
        {isOwner && (
          <TabsContent value="overview" className="mt-4"><Reports /></TabsContent>
        )}
        <TabsContent value="input" className="mt-4 space-y-8">
          <section>
            <h2 className="font-heading text-lg font-semibold mb-3">Daily totals — sales &amp; tips</h2>
            <Capture canViewDist={canViewDist} />
          </section>
        </TabsContent>
      </Tabs>
    </div>
  );
}
